import { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";

// Renders a TradingView-style candlestick chart with demand/supply zone overlays.
export function CandleChart({ candles = [], zones = [], levels = null, showZones = true, height = 420 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: { background: { type: "solid", color: "#121824" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace" },
      grid: { vertLines: { color: "#1E293B" }, horzLines: { color: "#1E293B" } },
      rightPriceScale: { borderColor: "#2D3D59" },
      timeScale: { borderColor: "#2D3D59", timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
      height,
      autoSize: true,
    });
    chartRef.current = chart;

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#22C55E", downColor: "#EF4444",
      borderUpColor: "#22C55E", borderDownColor: "#EF4444",
      wickUpColor: "#22C55E", wickDownColor: "#EF4444",
    });
    candleSeries.setData(candles);

    const volSeries = chart.addHistogramSeries({
      priceFormat: { type: "volume" }, priceScaleId: "",
      color: "#1E3A5F",
    });
    volSeries.priceScale().applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
    volSeries.setData(candles.map((c) => ({ time: c.time, value: c.volume, color: c.close >= c.open ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)" })));

    // SMA 20 overlay
    if (candles.length > 20) {
      const sma = [];
      for (let i = 19; i < candles.length; i++) {
        const slice = candles.slice(i - 19, i + 1);
        const avg = slice.reduce((s, c) => s + c.close, 0) / 20;
        sma.push({ time: candles[i].time, value: Number(avg.toFixed(2)) });
      }
      const smaSeries = chart.addLineSeries({ color: "#F59E0B", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      smaSeries.setData(sma);
    }

    // Demand/Supply zones rendered as proximal + distal price lines (NOT overlay area series).
    // Overlay area series were the confirmed CH-001 trigger: enabling them perturbed the chart's
    // visible-range/layout and compressed the candles. Price lines (identical to the Entry/SL/Target
    // levels, which never compress) mark each zone at its intended prices across the full time range
    // without adding out-of-range timestamps or affecting the time/price scale. GTF zone math is unchanged.
    if (showZones) {
      zones.forEach((z) => {
        const line = z.type === "demand" ? "#22C55E" : "#EF4444";
        const tag = z.type === "demand" ? "D" : "S";
        candleSeries.createPriceLine({
          price: z.proximal, color: line, lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: `${tag}·${z.pattern} prox`,
        });
        candleSeries.createPriceLine({
          price: z.distal, color: line, lineWidth: 1, lineStyle: 3,
          axisLabelVisible: true, title: `${tag} distal`,
        });
      });
    }

    // Entry / SL / Target lines
    if (levels) {
      candleSeries.createPriceLine({ price: levels.entry, color: "#3B82F6", lineWidth: 1, lineStyle: 0, axisLabelVisible: true, title: "Entry" });
      candleSeries.createPriceLine({ price: levels.stop_loss, color: "#EF4444", lineWidth: 1, lineStyle: 0, axisLabelVisible: true, title: "SL" });
      candleSeries.createPriceLine({ price: levels.target, color: "#22C55E", lineWidth: 1, lineStyle: 0, axisLabelVisible: true, title: "Target" });
    }

    // Defer the initial fit until the container has its final laid-out width.
    // autoSize settles a frame after mount; calling fitContent synchronously here runs against a
    // 0/stale width, which bunches the candles to the right edge with a large blank area on the left.
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (chartRef.current) chart.timeScale().fitContent();
      });
    });

    const handleResize = () => chart.timeScale().fitContent();
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      window.removeEventListener("resize", handleResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [candles, zones, levels, showZones, height]);

  return <div ref={containerRef} data-testid="candle-chart" className="w-full" style={{ height }} />;
}
